import { useCallback, useState } from "react";
import type {
  StatusMappingItem,
  StatusMappingViewMode,
  StatusMappingSectionViewModel,
  UseStatusMappingSectionViewModelArgs,
} from "./StatusMappingSection.ViewModel.types";

export const useStatusMappingSectionViewModel = ({
  items,
  mappings,
  boardStatuses,
  onChange,
}: UseStatusMappingSectionViewModelArgs): StatusMappingSectionViewModel => {
  const [viewMode, setViewMode] = useState<StatusMappingViewMode>("source");
  const [queries, setQueries] = useState<Record<string, string>>({});

  const toggleViewMode = useCallback(() => {
    setViewMode((prev) => (prev === "source" ? "column" : "source"));
    setQueries({});
  }, []);

  const setQuery = (id: string, value: string) =>
    setQueries((prev) => ({ ...prev, [id]: value }));

  const getMappedStatusIds = (sourceItemId: string): string[] =>
    mappings.filter((m) => m.sourceStatusId === sourceItemId).map((m) => m.boardStatusId);

  const getMappedStatusLabels = (sourceItemId: string): Array<{ id: string; name: string }> =>
    getMappedStatusIds(sourceItemId).flatMap((id) => {
      const status = boardStatuses.find((s) => s.id === id);
      return status ? [{ id: status.id, name: status.name }] : [];
    });

  const handleSelectionChange = useCallback(
    (item: StatusMappingItem, newBoardStatusIds: string[]) => {
      const next = mappings.filter((m) => m.sourceStatusId !== item.id);
      newBoardStatusIds.forEach((boardStatusId) => {
        next.push({ sourceStatusId: item.id, sourceStatusName: item.name, boardStatusId });
      });
      onChange(next);
    },
    [mappings, onChange],
  );

  const getMappedSourceIds = (columnId: string): string[] =>
    mappings.filter((m) => m.boardStatusId === columnId).map((m) => m.sourceStatusId);

  const getMappedSourceLabels = (columnId: string): Array<{ id: string; name: string }> =>
    getMappedSourceIds(columnId).flatMap((id) => {
      const item = items.find((i) => i.id === id);
      return item ? [{ id: item.id, name: item.name }] : [];
    });

  const handleColumnSelectionChange = useCallback(
    (column: { id: string; name: string }, newSourceIds: string[]) => {
      const next = mappings.filter((m) => m.boardStatusId !== column.id);
      newSourceIds.forEach((sourceId) => {
        const source = items.find((i) => i.id === sourceId);
        if (source) {
          next.push({
            sourceStatusId: sourceId,
            sourceStatusName: source.name,
            boardStatusId: column.id,
          });
        }
      });
      onChange(next);
    },
    [items, mappings, onChange],
  );

  return {
    viewMode,
    toggleViewMode,
    queries,
    setQuery,
    getMappedStatusIds,
    getMappedStatusLabels,
    handleSelectionChange,
    getMappedSourceIds,
    getMappedSourceLabels,
    handleColumnSelectionChange,
  };
};
